import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { tailwind } from "@/utilities/tailwind-utils";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem
} from "@/components/ui/sidebar";
import { ChevronDownIcon, ChevronRightIcon } from "@/utilities/icon-utils";
import type { NavigationItem } from "@/types/core";

/**
 * True when `pathname` is this item's page or somewhere beneath it.
 *
 * Prefix-matched on a path boundary rather than `startsWith` alone, so
 * `/admin/fees` does not light up for `/admin/fees-archive`.
 */
function isWithin(pathname: string, url: string): boolean {
  return pathname === url || pathname.startsWith(`${url}/`);
}

function visible(items: NavigationItem[] = []): NavigationItem[] {
  return items.filter((item) => !item.hidden);
}

/**
 * A section whose children are pages of their own.
 *
 * The parent row toggles rather than navigates: a group exists to hold its children,
 * and several groups (Operations) have no page of their own to land on. Where a section
 * root is a real page it appears as the first child, so it stays reachable.
 */
function NavGroup({
  item,
  pathname
}: {
  item: NavigationItem;
  pathname: string;
}) {
  const children = useMemo(() => visible(item.children), [item.children]);
  const holdsCurrentPage = useMemo(
    () => children.some((child) => isWithin(pathname, child.url)),
    [children, pathname]
  );

  // Null until the operator decides for themselves; until then the group follows the
  // route. Derived rather than synced in an effect, so navigating into a section opens
  // it without a render-then-correct flash.
  const [toggled, setToggled] = useState<boolean | null>(null);
  const open = toggled ?? holdsCurrentPage;

  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        tooltip={item.title}
        aria-expanded={open}
        onClick={() => setToggled(!open)}
        // The section itself is never "the current page" — one of its children is.
        data-active={false}
      >
        {item.icon && <item.icon />}
        <span>{item.title}</span>
        <Chevron className="ml-auto size-4 shrink-0" />
      </SidebarMenuButton>

      {open && (
        <SidebarMenuSub>
          {children.map((child) => (
            <SidebarMenuSubItem key={child.title}>
              <SidebarMenuSubButton
                asChild
                isActive={isWithin(pathname, child.url)}
              >
                <Link to={child.url}>
                  {child.icon && <child.icon />}
                  <span>{child.title}</span>
                </Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}

export function NavMain({ items }: { items: NavigationItem[] }) {
  const location = useLocation();
  const visibleItems = useMemo(() => visible(items), [items]);

  return (
    <SidebarGroup>
      <SidebarGroupContent className={tailwind.layout.flexColGap2}>
        <SidebarMenu>
          {visibleItems.map((item) =>
            visible(item.children).length > 0 ? (
              <NavGroup
                key={item.title}
                item={item}
                pathname={location.pathname}
              />
            ) : (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  tooltip={item.title}
                  // Exact match: a dashboard at "/admin" must not stay highlighted
                  // while the operator is on "/admin/users".
                  data-active={location.pathname === item.url}
                >
                  <Link to={item.url}>
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
